import configurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { translate } from 'services/i18nService';
import {
  GAME_INFO_RESPONSE,
  GAME_RESUMED,
  GAME_RESUME_ERROR,
  GAME_SAVED,
} from 'services/MessagingService/EventNames';
import getMessagingServiceInstance from 'services/MessagingService/MessagingService';
import NotificationService from 'services/NotificationService';
import ApiService from 'services/ApiService/ApiService';
import { GAMES } from 'utils/consts';
import parseCatch from 'utils/parseCatch';
import { User } from 'stores/UserStore/UserStore.types';

import TokenKeno from './TokenKeno';
import RollTheDice from './RollTheDice';
import SpinTheWheel from './SpinTheWheel';
import TipMenu from './TipMenu';
import Game from './Game';


export const GAME_INFO_URL = '{game}';
export const RESUME_GAME_URL = '{gameName}/resume';
export const ROLL_THE_DICE_ACTION = 'RollTheDice/roll';
export const ROLL_THE_DICE_START = 'RollTheDice/start';
export const ROLL_THE_DICE_STOP = 'RollTheDice/stop';
export const SPIN_THE_WHEEL_START = 'SpinTheWheel/start';
export const SPIN_THE_WHEEL_STOP = 'SpinTheWheel/stop';
export const SPIN_THE_WHEEL_URL = 'SpinTheWheel/roll';
export const TIP_MENU_ACTION_URL = 'TipMenu/tip';
export const TIP_MENU_START_URL = 'TipMenu/start';
export const TIP_MENU_STOP_URL = 'TipMenu/stop';
export const TOKEN_KENO_ACTION = 'TokenSquares/click';
export const TOKEN_KENO_START = 'TokenSquares/start';
export const TOKEN_KENO_STOP = 'TokenSquares/stop';

export const UPDATE_GAME_SETTINGS_URL = '{game}/update';

export const computeEndpoint = (
  username: User['username'],
  endpoint = ''
): string => {
  let server = configurationService.get(ConfigurationKeys.ACTIVE_GAME_SERVER);
  if (!server) {
    server = 'https://omniappz.com/apps/rest/v1/app/cam4';
  }

  return encodeURI([server, username, endpoint].join('/'));
};

export type Payload = Partial<{
  o: Record<PropertyKey, any>,
  p: number,
  token: string
}>;

export default class GamesService extends Game {
  tokenKeno: TokenKeno;
  rollTheDice: RollTheDice;
  spinTheWheel: SpinTheWheel;
  tipMenu: TipMenu;

  constructor() {
    super(getMessagingServiceInstance());
    this.tokenKeno = new TokenKeno();
    this.rollTheDice = new RollTheDice();
    this.spinTheWheel = new SpinTheWheel();
    this.tipMenu = new TipMenu();
  }

  isGameWithNotifications = ({ name }: { name: string }): boolean => [
    GAMES.TIP_MENU,
    GAMES.ROLL_THE_DICE,
    GAMES.SPIN_THE_WHEEL,
    GAMES.TOKEN_SQUARES,
  ].includes(name);

  getGameInfo = async({ game, roomName, token }:
    { game: string; roomName: string; token: string; }
  ): Promise<void> => {
    const url = ApiService.substitute(GAME_INFO_URL, { game });
    const computedEndpoint = computeEndpoint(roomName, url);
    const params = { token };

    try {
      const response = await ApiService.get(computedEndpoint, {
        params,
        withCredentials: false,
      });
      this.messagingService.emit(GAME_INFO_RESPONSE, response.data);
    } catch (error) {
      parseCatch(error);
    }
  };

  handleGameStopStartAnotherGame = async(
    data: {
      roomName: string;
      token: string;
      price: string;
    },
    currentGame: string,
    newGame: string
  ): Promise<void> => {
    const { roomName, token, price, ...rest } = data;
    const params = { token };
    const tokenKenoPayload: Payload = {
      o: Object.values(rest),
      token,
    };
    const otherGamesPayload: Payload = {
      o: rest,
      p: parseInt(price, 10),
      token,
    };

    const gamesStopUrlsMap = {
      [GAMES.TOKEN_SQUARES]: TOKEN_KENO_STOP,
      [GAMES.ROLL_THE_DICE]: ROLL_THE_DICE_STOP,
      [GAMES.SPIN_THE_WHEEL]: SPIN_THE_WHEEL_STOP,
    };
    const gamesStartUrlsMap = {
      [GAMES.TOKEN_SQUARES]: TOKEN_KENO_START,
      [GAMES.ROLL_THE_DICE]: ROLL_THE_DICE_START,
      [GAMES.SPIN_THE_WHEEL]: SPIN_THE_WHEEL_START,
    };

    const stopURL = computeEndpoint(roomName, gamesStopUrlsMap[currentGame]);
    const startURL = computeEndpoint(roomName, gamesStartUrlsMap[newGame]);

    let requestPayload = otherGamesPayload;
    if (newGame === GAMES.TOKEN_SQUARES) {
      requestPayload = tokenKenoPayload;
    }

    try {
      await ApiService.post(stopURL, {
        params,
        data: params,
        withCredentials: false,
      });

      await ApiService.post(startURL, {
        params,
        data: requestPayload,
        withCredentials: false,
      });
    } catch (error) {
      parseCatch(error);
    }
  };

  saveGameSettings = async(
    { roomName, gameName, token, options, price }: {
      roomName: string;
      gameName: string;
      token: string;
      options: Array<Record<PropertyKey, any>>;
      price: string;
    }): Promise<void> => {
    let data: Payload = {
      token,
      o: options,
      p: parseInt(price, 10),
    };

    if (gameName === GAMES.TOKEN_SQUARES) {
      const parsedOptions = options.filter(({ p }) => p !== undefined);
      data = {
        token,
        o: parsedOptions,
      };
    }

    const url = ApiService.substitute(UPDATE_GAME_SETTINGS_URL, { game: gameName });
    const computedEndpoint = computeEndpoint(roomName, url);
    const params = { token };

    try {
      await ApiService.post(computedEndpoint, {
        params,
        data,
        withCredentials: false,
      });
      this.messagingService.emit(GAME_SAVED);
      NotificationService.create({
        type: NotificationService.type.TOAST,
        subtype: NotificationService.subtype.SUCCESS,
        text: translate('v2broadcast.changes.saved'),
      });
    } catch (error) {
      parseCatch(error);
    }
  };

  resumeGame = async(
    roomName: string,
    gameName: string,
    token: string
  ): Promise<void> => {
    const url = ApiService.substitute(RESUME_GAME_URL, { gameName });
    const computedEndpoint = computeEndpoint(roomName, url);
    const params = { token };
    const data = { token };

    try {
      await ApiService.post(computedEndpoint, {
        data,
        params,
        withCredentials: false,
      });
      this.messagingService.emit(GAME_RESUMED);
    } catch (error) {
      this.messagingService.emit(GAME_RESUME_ERROR);
    }
  };
}
